/* eslint-disable react/jsx-pascal-case */
import { useMemo, useState, useEffect, useRef } from 'react';
import styles from './MTableComponents.module.scss';
import {
  TextField,
  Box,
  Icon,
  FormControl,
  MenuItem,
  Select,
  Autocomplete,
} from '@mui/material';
import moment from 'moment';
import ClearIcon from '@mui/icons-material/Clear';
import { Text } from '../typography';
import SimpleUserCard from '../SimpleUserCard';
import { getLocalTime } from '../../utilities/time';
import Label from '../action_items/Label';
import defaultLogo from '../../assets/workspace_image.png';
import { sortAlphabeticalOrder, vehicleString } from '../../utilities';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys';
import { useWorkspacePermissions } from '../../providers/WorkspacePermissionsProvider';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import { useQueryParams } from '../../hooks/misc';
import { useLocation } from 'react-router-dom';
import { tableSortingParams, getFilterChipProps } from './table';
import { Form } from 'react-bootstrap';
import noImage from '../../assets/icons/image_blank.png';
import { useCurrentUser } from '../../providers/UserProvider';
import { useWorkspace } from '../../providers/WorkspaceProvider';
import MultiAvatar from '../MultiAvatar';
import SimpleWorkspaceCard from '../SimpleWorkspaceCard';
import { pastDue } from '../../utilities/audits';
import { useLabels } from '../../providers/LabelProvider';
import { v4 as uuidv4 } from 'uuid';
import { dateTypes } from './table';

const dateFormatWithTime = 'dddd MMM Do YYYY - h:mma'; // hh is 12hr clock, a shows am/pm at the end
const dateFormatWithTimeMilitary = 'dddd MMM Do YYYY - HH:mm';
const dateFormatWithoutTime = 'dddd MMM Do YYYY';
const maxLabels = 3;

export const FilterChip = ({ filter, setFilters, listName }) => {
  const { labels } = useLabels();
  const { allWorkspaces, allVehicles } = useWorkspace();
  const { allUsers } = useWorkspacePermissions();
  const {
    company: { riskMatrix },
    allRoles,
  } = useCurrentUser();
  const { label, values } = getFilterChipProps({
    filter,
    workspaces: allWorkspaces,
    users: allUsers,
    labels,
    vehicles: allVehicles,
    listName,
    riskLevels: riskMatrix?.levels,
    roles: allRoles,
  });
  const { t } = useTranslation();
  const dateKeys = dateTypes.map(([key]) => key);
  return (
    <div className={styles.filterChipContainer}>
      <Text noMargin weight="semiBold">
        {t(label)}:
      </Text>
      {values?.length > 0 &&
        values?.map((v) => (
          <Chip
            key={`chip-${uuidv4()}`}
            label={typeof v === 'string' ? t(v) : v}
            sx={{ backgroundColor: '#fff' }}
            onDelete={() => {
              const key = filter?.id;
              const valueToFilter =
                typeof v === 'string'
                  ? v
                  : v.props[key]?.id ??
                    v.props?.user?.id ??
                    v.props?.workspace?.id ??
                    v.props?.value;

              setFilters((prev) => {
                const filterToChange = prev.find((f) => f.id === key);

                if (typeof filterToChange.value !== 'string') {
                  const valueType = typeof filterToChange.value[0];
                  const updatedValue = dateKeys.includes(key)
                    ? filterToChange.value.map((d) =>
                        valueToFilter === d ? '' : d,
                      )
                    : filterToChange.value.filter((v) =>
                        valueType === 'string'
                          ? v !== valueToFilter
                          : v !== parseInt(valueToFilter),
                      );

                  const updatedFilters = [
                    ...prev.filter(({ id }) => id !== key),
                  ];
                  if (
                    updatedValue?.length > 0 &&
                    !updatedValue.every((v) => v === '')
                  ) {
                    updatedFilters.push({ id: key, value: updatedValue });
                  }
                  return updatedFilters;
                }

                return prev.filter((p) => p.id !== key);
              });
            }}
          />
        ))}
    </div>
  );
};

export const NumberFilter = ({ column, filterBy }) => (
  <TextField
    type="number"
    value={column.getFilterValue() ?? ''}
    onChange={(e) => column.setFilterValue(e.target.value)}
    variant="standard"
    placeholder={`Filter by ${filterBy}`}
  />
);

export const CustomDateRangeFilter = ({
  column,
  minDate,
  maxDate,
  setMinDate,
  setMaxDate,
}) => {
  const handleMinDateChange = (event) => {
    const value = event.target.value;
    const formattedValue = moment(value).format('YYYY-MM-DD');
    setMinDate(formattedValue);
    column.setFilterValue([formattedValue, maxDate]);
  };

  const handleMaxDateChange = (event) => {
    const value = event.target.value;
    const formattedValue = moment(value).format('YYYY-MM-DD');
    setMaxDate(formattedValue);
    column.setFilterValue([minDate, formattedValue]);
  };
  const clearMinDate = () => {
    setMinDate('');
    column.setFilterValue(['', maxDate]);
  };

  const clearMaxDate = () => {
    setMaxDate('');
    column.setFilterValue([minDate, '']);
  };

  return (
    <Box display="flex" maxWidth={'20rem'} borderBottom={'1px solid gray'}>
      <TextField
        type="date"
        value={minDate || ''}
        onChange={handleMinDateChange}
        label="Min"
        InputLabelProps={{ shrink: true }}
        InputProps={{
          className: !!minDate ? styles.dateSelector : styles.placeholder,
          style: { fontSize: 'small' },
          placeholder: 'Min',
          endAdornment: !!minDate ? (
            <ClearIcon
              className={styles.clear}
              fontSize="1rem"
              onClick={clearMinDate}
            />
          ) : null,
        }}
      />
      <TextField
        type="date"
        value={maxDate || ''}
        label="Max"
        onChange={handleMaxDateChange}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          className: !!maxDate ? styles.dateSelector : styles.placeholder,
          style: { fontSize: 'small' },
          endAdornment: !!maxDate ? (
            <ClearIcon
              className={styles.clear}
              fontSize="1rem"
              onClick={clearMaxDate}
            />
          ) : null,
        }}
      />
    </Box>
  );
};

export const formatFilters = (originalField, originalValue) => {
  let operator = 'eq';
  let value = originalValue;
  let field = originalField;
  switch (originalField) {
    case 'description':
    case 'title':
      operator = 'iLike';
      value = `%${value}%`;
      break;
    case 'reviewer':
    case 'creator':
    case 'workspace':
      field = `${field}Id`;
      break;
    case 'dateTime':
    case 'dateCreated':
      const [min, max] = originalValue;
      const dateMin =
        min && moment(min).isValid()
          ? moment(min).format('YYYY-MM-DD 00:00:00')
          : null;
      const dateMax =
        max && moment(max).isValid()
          ? moment(max).format('YYYY-MM-DD 23:59:59')
          : null;
      if (dateMin && !dateMax) {
        operator = 'gte';
        value = [dateMin];
      } else if (!dateMin && dateMax) {
        operator = 'lte';
        value = [dateMax];
      } else if (dateMin && dateMax) {
        operator = 'between';
        value = [dateMin, dateMax];
      } else {
        return null;
      }
      break;
    default:
      break;
  }
  return { field, operator, value };
};

export const useTableComponents = () => {
  const { t } = useTranslation();
  const { allUsers, allWorkspaceUsers, companyAdmins } =
    useWorkspacePermissions();
  const { user, isAdmin } = useCurrentUser();
  const { availableWorkspaces, allWorkspaces } = useWorkspace();
  const { getParam } = useQueryParams();

  const multiAvatarCell = ({ cell, formattedUsers }) => {
    const users = formattedUsers ?? cell?.getValue();
    if (users?.length > 1) {
      return (
        <div style={{ marginLeft: '0.4rem' }}>
          <MultiAvatar users={users} maxLength={5} size="xs" />
        </div>
      );
    } else if (users?.length === 1) {
      return <SimpleUserCard weight="semiBold" user={users[0]} size="sm" />;
    } else {
      return (
        <Text noMargin size="sm" color="secondary">
          {t(keys.common.NONE)}
        </Text>
      );
    }
  };

  const assetFilterOptions = (data) => {
    return data.map((u) => ({
      label: vehicleString(u),
      value: `${u.id}`,
    }));
  };
  const assetCell = ({ cell, weight = 'semiBold' }) =>
    !!cell.getValue() ? (
      <div className={styles.workspace}>
        <img
          className={styles.workspaceLogo}
          src={cell.getValue()?.imageUrl || noImage}
          alt="vehicle"
        />
        <Text noMargin size="sm" truncate weight={weight}>
          {vehicleString(cell.getValue())}
        </Text>
      </div>
    ) : (
      <Text noMargin color="secondary" size="sm">
        {t(keys.common.NONE)}
      </Text>
    );
  const textCell = ({
    cell,
    weight = 'semiBold',
    color = 'primary',
    size = 'sm',
  }) => {
    const filterValue = cell.column.getFilterValue();
    const rawTerm =
      typeof filterValue === 'string'
        ? filterValue.replace(/^%|%$/g, '')
        : null;
    const wordsToHighlight = rawTerm ? [rawTerm.toLowerCase()] : [];
    return cell.getValue() !== null && !!cell.getValue() ? (
      <Text
        noMargin
        weight={weight}
        size={size}
        truncate
        color={color}
        highlight={!!wordsToHighlight.length}
        wordsToHighlight={wordsToHighlight}
      >
        {String(cell.getValue())}
      </Text>
    ) : (
      <Text noMargin color="secondary" size={size}>
        {t(keys.common.NONE)}
      </Text>
    );
  };

  const customTextCell = (
    value,
    color = 'primary',
    weight = 'semiBold',
    size = 'sm',
    wordsToHighlight = [],
  ) =>
    value ? (
      <Text
        noMargin
        size={size}
        truncate
        color={color}
        weight={weight}
        highlight={!!wordsToHighlight.length}
        wordsToHighlight={wordsToHighlight}
      >
        {value}
      </Text>
    ) : (
      <Text noMargin color="secondary" size="sm">
        {t(keys.common.NONE)}
      </Text>
    );

  const externalIdCell = ({
    id,
    externalId,
    size = 'sm',
    weight = 'semiBold',
  }) =>
    !id ? (
      <Text noMargin color="secondary" size={size}>
        {t(keys.common.NONE)}
      </Text>
    ) : (
      <Text noMargin size={size} weight={weight}>
        {externalId ||
          `${user.company?.incidentName || 'INC'}-${String(id).padStart(
            5,
            '0',
          )}`}
      </Text>
    );

  const userCell = ({ cell, props = { weight: 'semiBold', size: 'sm' } }) =>
    cell.getValue() ? (
      <SimpleUserCard
        disableClick
        size={props.size}
        user={cell.getValue()}
        weight={props.weight || 'semiBold'}
      />
    ) : (
      <Text noMargin size="sm" color="secondary">
        {t(keys.common.NONE)}
      </Text>
    );
  const dateCell = (
    { cell },
    {
      color = 'primary',
      dateOnly = false,
      format = false,
      weight = 'semiBold',
      size = 'sm',
    } = {},
  ) => {
    const isMilitary = user?.timeSetting === 'military';
    const displayFormat = format
      ? format
      : dateOnly
      ? dateFormatWithoutTime
      : isMilitary
      ? dateFormatWithTimeMilitary
      : dateFormatWithTime;

    return !!cell.getValue() ? (
      <Text noMargin size={size} color={color} weight={weight}>
        {getLocalTime(cell.getValue()).format(displayFormat)}
      </Text>
    ) : (
      <Text size={size} noMargin color="secondary">
        {t(keys.common.NONE)}
      </Text>
    );
  };
  const dateCellFromNow = ({ cell, weight = 'semiBold', size = 'sm' }) => {
    const date =
      !!cell.getValue() && cell.getValue()?.includes('-')
        ? cell.getValue()
        : parseInt(cell.getValue());
    const overdue = !!date ? pastDue(date) : false;
    return !!date ? (
      <Text
        noMargin
        color={!!overdue ? 'red' : 'primary'}
        size={size}
        weight={weight}
      >
        {getLocalTime(cell.getValue()).fromNow()}
      </Text>
    ) : (
      <Text size="sm" color="secondary" noMargin>
        {t(keys.common.NONE)}
      </Text>
    );
  };
  const labelCell = ({ name, color, rowSized = true }) => {
    return (
      <Label
        name={name || 'unknown'}
        color={color || 'grey'}
        truncate={false}
        rowSized={rowSized}
      />
    );
  };

  const labelCellWithIcon = ({
    name,
    labelColor,
    icon,
    iconColor,
    show = false,
  }) => {
    return (
      <div className={styles.labelCellWithIcon}>
        <Label name={name || 'unknown'} color={labelColor || 'grey'} />
        {show && (
          <Icon
            style={{
              fontSize: '1.4rem',
              color: iconColor,
            }}
          >
            {icon}
          </Icon>
        )}
      </div>
    );
  };

  const multiLabelCell = ({ labels }) => {
    return (
      <div className={styles.labelContainer}>
        {labels?.slice(0, maxLabels).map((label) => (
          <Label
            key={label.id}
            className={styles.label}
            name={label.name}
            color={label.color.toLowerCase()}
          />
        ))}
        {labels?.length > maxLabels && (
          <Text
            size="sm"
            weight="semiBold"
            noMargin
            color="accentPrimary"
          >{`+ ${labels.length - maxLabels}`}</Text>
        )}
      </div>
    );
  };

  const userFilterOptions = (type = 'workspace', includeAnon = false) => {
    const allWorkspaces = getParam('allWorkspaces');
    const usersArray =
      type === 'all' || allWorkspaces === 'TRUE'
        ? [
            ...allUsers,
            includeAnon
              ? { id: 'null', firstName: 'Anonymous', lastName: '' }
              : undefined,
          ]
        : [
            ...allWorkspaceUsers,
            ...companyAdmins,
            includeAnon ? { id: 'null', firstName: 'Anonymous' } : undefined,
          ];

    const usersMap = new Map();
    usersArray.forEach((u) => usersMap.set(u?.id, u));
    const u = Array.from(usersMap?.values())
      ?.filter(Boolean)
      ?.map((u) => ({
        ...u,
        label: `${u.firstName} ${u.lastName}`,
      }));

    const sorted = sortAlphabeticalOrder(u, 'label');
    return sorted || u;
  };

  const workspaceCell = ({ cell, weight = 'semiBold', size = 'sm' }) => {
    return (
      <div className={styles.workspace}>
        {cell.getValue()?.title ? (
          <>
            <img
              src={cell.getValue()?.logoUrl || defaultLogo}
              alt="workspace_img"
              className={styles.workspaceLogo}
            />
            <Text noMargin size={size} truncate weight={weight}>
              {cell.getValue()?.title}
            </Text>
          </>
        ) : (
          <div className={styles.workspace}>
            <Icon
              style={{
                fontSize: size === 'sm' ? '1.2rem' : '1rem',
                color: 'blue',
              }}
            >
              public
            </Icon>
            <Text weight={weight} noMargin size={size}>
              {t(keys.common.SHARED)}
            </Text>
          </div>
        )}
      </div>
    );
  };

  const currentWorkspaceCell = ({ cell, original }) => {
    const workspaces = cell.getValue() || [];
    const lastWorkspace =
      workspaces.find((w) => w.id === original.lastWorkspaceId) ??
      workspaces[0];
    return (
      <div className={styles.workspace}>
        {lastWorkspace?.title ? (
          <>
            <img
              src={lastWorkspace.logoUrl || defaultLogo}
              alt="workspace_img"
              className={styles.workspaceLogo}
            />
            <Text noMargin size="sm" truncate weight="semiBold">
              {lastWorkspace.title}
            </Text>
          </>
        ) : (
          <Text noMargin size="sm" truncate color="secondary">
            {t(keys.common.NONE)}
          </Text>
        )}
      </div>
    );
  };
  const workspaceFilterOptions = ({
    includePublic = false,
    hasCompanyWideAccess = false,
  } = {}) => {
    return includePublic
      ? [
          { id: 'null', title: 'Shared' },
          ...(hasCompanyWideAccess ? allWorkspaces : availableWorkspaces),
        ]
      : hasCompanyWideAccess || isAdmin
      ? allWorkspaces
      : availableWorkspaces;
  };

  const dropdownCell = ({
    valueStrings,
    values,
    isValueInt = false,
    selected,
    onChange = () => {},
    rowId,
    disabled = false,
  }) => {
    const options = Object.keys(values).map((key) => (
      <option key={key} value={values[key]}>
        {valueStrings[key]}
      </option>
    ));

    return (
      <div
        className={styles.dropdownCell}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <Form.Select
          value={selected}
          disabled={disabled}
          onChange={(e) => {
            onChange(
              rowId,
              isValueInt ? parseInt(e.target.value) : e.target.value,
            );
          }}
        >
          {options}
        </Form.Select>
      </div>
    );
  };

  const multiTextCell = ({ cell }, color = 'primary') => {
    const roles = cell.getValue();
    const roleTitles =
      roles?.length > 0
        ? roles.map(({ title }) => title).join(', ')
        : t(keys.common.NONE);

    const filterValue = cell.column.getFilterValue();
    const rawTerm =
      typeof filterValue === 'string'
        ? filterValue.replace(/^%|%$/g, '')
        : null;
    const wordsToHighlight = rawTerm ? [rawTerm.toLowerCase()] : [];

    return (
      <Text
        noMargin
        size="sm"
        truncate
        color={color}
        weight="semiBold"
        highlight={!!wordsToHighlight.length}
        wordsToHighlight={wordsToHighlight}
      >
        {roleTitles}
      </Text>
    );
  };
  const groupedCell = ({ row, component }) => {
    const total = row.getLeafRows()?.length || 0;
    const hasSubGroup = row?.subRows?.length > 0 ?? false;
    return (
      <div className={styles.groupedCell}>
        <Chip
          label={hasSubGroup ? row?.leafRows?.length : total}
          sx={{ fontSize: 12 }}
          size="small"
        />
        <div className={styles.component}>{component}</div>
      </div>
    );
  };
  return {
    textCell,
    customTextCell,
    userCell,
    dateCell,
    dateCellFromNow,
    userFilterOptions,
    labelCell,
    labelCellWithIcon,
    multiLabelCell,
    workspaceCell,
    currentWorkspaceCell,
    dropdownCell,
    assetCell,
    assetFilterOptions,
    externalIdCell,
    multiTextCell,
    workspaceFilterOptions,
    multiAvatarCell,
    groupedCell,
  };
};

export const tableStyle = {
  muiTableHeadCellProps: () => ({
    style: {
      fontSize: '12px',
      border: 'none',
    },
  }),
  muiTablePaperProps: () => ({
    style: {
      border: 'none',
      padding: '1rem',
      boxShadow: 'none',
      width: '100%',
      borderRadius: '10px',
    },
  }),
};

export const tableOptions = {
  enableColumnOrdering: true,
  enableColumnResizing: true,
  enableSorting: true,
  manualPagination: true,
  enableGlobalFilter: false,
  manualFiltering: true,
  manualSorting: true,
  enableFilterMatchHighlighting: false,
  enableFacetedValues: true,
  enableStickyHeader: true,
};
export const checkDueDateColor = ({ dueDate, status }) => {
  const today = new moment();
  if (status === 'DONE' || status === 'DUPLICATE') {
    return 'secondary';
  }
  if (status !== 'DONE' && dueDate) {
    if (moment(parseInt(dueDate)).isBefore(today, 'day')) {
      return 'red';
    }
    if (
      moment(parseInt(dueDate)).isSame(today, 'day') ||
      moment(parseInt(dueDate)).isSame(today.add(1, 'day'), 'day')
    ) {
      return 'yellow';
    }
  } else {
    return 'primary';
  }
};

export const LabelFilter = ({
  column,
  selectedLabels,
  labels,
  handleChange,
  handleRemove,
}) => {
  const filteredLabels = useMemo(() => {
    return labels.filter((l) => !selectedLabels?.some((m) => m.id === l.id));
  }, [labels, selectedLabels]);

  return (
    <Box
      display="flex"
      maxWidth={'20rem'}
      minHeight={'2rem'}
      height={'auto'}
      alignItems={'center'}
    >
      <FormControl variant="standard" sx={{ minWidth: 120, width: '100%' }}>
        <Select
          SelectDisplayProps={{}}
          value={selectedLabels}
          onChange={(e) => {
            handleChange(e.target.value);
            column.setFilterValue(e.target.value.map(({ id }) => id));
          }}
          multiple
          renderValue={(labels) => (
            <div className={styles.selectedLabels}>
              {labels.map((label) => (
                <Label
                  key={label.id}
                  name={label.name}
                  color={label.color}
                  className={styles.labelFilter}
                />
              ))}
            </div>
          )}
        >
          {filteredLabels.map((label) => (
            <MenuItem value={label} key={label.id}>
              <Label key={label.id} name={label.name} color={label.color} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {selectedLabels?.length > 0 && (
        <Icon className={styles.close} onClick={() => handleRemove()}>
          close
        </Icon>
      )}
    </Box>
  );
};

export const AutocompleteFilter = ({ column, items, filterBy, type }) => {
  const { getParam, deleteParam } = useQueryParams();
  const urlValue = getParam(column.id);

  const urlValues = useMemo(
    () =>
      typeof urlValue === 'string'
        ? urlValue.split(',').filter(Boolean)
        : urlValue !== undefined
        ? [String(urlValue)]
        : [],
    [urlValue],
  );

  const { allUsers } = useWorkspacePermissions();
  const { allWorkspaces } = useWorkspace();

  const userObjects = useMemo(
    () => allUsers.filter((u) => urlValues.includes(String(u.id))),
    [allUsers, urlValues],
  );
  const workspaceObjects = useMemo(
    () => (allWorkspaces ?? []).filter((w) => urlValues.includes(String(w.id))),
    [allWorkspaces, urlValues],
  );

  const [inputValue, setInputValue] = useState('');

  const prevFilterRef = useRef();
  const objectTypes = ['user', 'workspace'];
  const [value, setValue] = useState([]);

  const hasPrepopulated = useRef(false);

  useEffect(() => {
    if (hasPrepopulated.current) return;
    let nextValue = [];
    if (type === 'workspace') {
      nextValue = workspaceObjects;
    } else if (type === 'user') {
      nextValue = userObjects;
    } else if (urlValues.length) {
      nextValue = items.filter((opt) =>
        urlValues.includes(String(opt.value ?? opt.id)),
      );
    }
    if (nextValue.length) {
      setValue(nextValue);
      hasPrepopulated.current = true;
    }
    // eslint-disable-next-line
  }, [type, urlValue, allUsers, allWorkspaces, items]);

  const CustomTag = ({ option, type }) => (
    <Box key={option.id}>
      <div className={styles.customChipContainer}>
        {type === 'user' ? (
          <SimpleUserCard user={option} size="sm" />
        ) : type === 'workspace' ? (
          <SimpleWorkspaceCard workspace={option} size="sm" />
        ) : (
          <Text size="sm" weight="semiBold">
            {option?.label}
          </Text>
        )}
        <Icon
          onClick={() => {
            deleteParam(column.id);
            setValue((prev) =>
              prev.filter((item) =>
                objectTypes.includes(type)
                  ? item?.id !== option?.id
                  : item?.value !== option?.value,
              ),
            );
          }}
          className={styles.close}
        >
          remove
        </Icon>
      </div>
    </Box>
  );

  useEffect(() => {
    const newFilterValue = objectTypes.includes(type)
      ? value.map((item) => item.id)
      : value.map((item) => item.value);

    if (
      JSON.stringify(newFilterValue) !== JSON.stringify(prevFilterRef.current)
    ) {
      prevFilterRef.current = newFilterValue;
      column.setFilterValue(newFilterValue.length > 0 ? newFilterValue : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, type]);

  useEffect(() => {
    const currentFilters = column.getFilterValue();

    if (currentFilters === undefined) {
      setValue([]);
      setInputValue('');
    }
  }, [column]);

  return (
    <Box
      display="flex"
      maxWidth="20rem"
      minHeight="2rem"
      height="auto"
      alignItems="center"
    >
      <FormControl variant="standard" sx={{ width: '100%' }}>
        <Autocomplete
          multiple
          options={items}
          isOptionEqualToValue={(option, value) =>
            objectTypes?.includes(type)
              ? option?.id === value?.id
              : option?.value === value?.value
          }
          filterSelectedOptions
          value={value || userObjects}
          inputValue={inputValue}
          onChange={(event, newValue) => {
            setValue(newValue);
          }}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          getOptionLabel={(option) =>
            type === 'workspace' ? option.title : option.label
          }
          renderTags={(value, getTagProps) => {
            const values = value.map((option, index) => (
              <CustomTag option={option} type={type} />
            ));

            const additionalChip = (
              <Chip
                key={`additional-chip-key${value?.id}`}
                label={`+${values?.length - 2}`}
                sx={{
                  fontSize: '0.75rem',
                  height: '1.5rem',
                  mb: 0.5,
                }}
              />
            );

            return values?.length > 2
              ? [...values?.slice(0, 2), additionalChip]
              : values;
          }}
          renderOption={(props, option) => (
            <Box {...props} key={option.id}>
              {type === 'user' ? (
                <SimpleUserCard user={option} disableClick size="sm" />
              ) : type === 'workspace' ? (
                <SimpleWorkspaceCard workspace={option} size="sm" />
              ) : (
                <Text noMargin size="sm" weight="semiBold">
                  {option.label}
                </Text>
              )}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              placeholder={`Filter by ${filterBy}`}
            />
          )}
        />
      </FormControl>
    </Box>
  );
};

const tableStatus = (tab, type) => {
  switch (type) {
    case 'assessments':
    case 'templates':
      return ['isDraft', tab === 1 ? true : false];
    case 'cpa':
      return ['open', tab === 1 ? true : false];
    default:
      return null;
  }
};

export const tableLabels = {
  templates: ['All', 'Draft', 'Published'],
  assessments: ['All', 'In Progress', 'Complete'],
  cpa: ['All', 'Open', 'Closed'],
};

export function TableTabs({ parent, onChange }) {
  const [value, setValue] = useState(0);
  const labels = tableLabels[parent];
  const { upsertParam, deleteParam } = useQueryParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const handleChange = async (event, newValue) => {
    setValue(newValue);

    const deletionPromises = [];
    for (const key of Array.from(searchParams.keys())) {
      if (!tableSortingParams.includes(key)) {
        deletionPromises.push(deleteParam(key));
      }
    }
    await Promise.all(deletionPromises);

    onChange();
    const filters = tableStatus(newValue, parent);
    if (newValue !== 0 && filters) {
      upsertParam(filters[0], filters[1]);
    }
  };

  return (
    <div className={styles.tabsContainer}>
      <Tabs value={value} onChange={handleChange}>
        {labels?.map((label, idx) => (
          <Tab key={label} label={label} value={idx} />
        ))}
      </Tabs>
    </div>
  );
}
export const privateColumn = {
  accessorKey: 'private',
  header: undefined,
  size: 40,
  filterVariant: 'select',
  filterSelectOptions: [
    { label: 'Private', value: `${true}` },
    { label: 'Public', value: `${false}` },
  ],
  Cell: ({ cell }) => {
    return (
      <Icon
        baseClassName="material-icons-outlined"
        sx={{
          opacity: 0.5,
          fontSize: '1.25rem',
        }}
      >
        {!cell.getValue() ? 'visibility' : 'visibility_off'}
      </Icon>
    );
  },
};
